// Bulandshahr — main site shell. Southwestern-inspired: ink utility bar, paper masthead,
// ink main-nav with gold hover underline, gold-accented ink footer. Serif display type + sans body.

import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import InstituteSeo from "@/components/InstituteSeo";
import api from "@/lib/api";
import { PALETTE, FONT, slugify } from "./_shared";
import AdmissionInquiryModal from "./AdmissionInquiryModal";
import InstituteSwitcher from "@/components/InstituteSwitcher";
import AboutMenu from "@/components/AboutMenu";
import {
  Menu, X, Phone, Mail, MapPin, Facebook, Instagram, Linkedin, Youtube,
  ChevronDown, ExternalLink, Search, ArrowRight,
} from "lucide-react";

const BulContext = createContext(null);
export const useBul = () => useContext(BulContext);

// Re-export PALETTE so older imports keep working.
export { PALETTE };

// Utility-bar audience quick links (Southwestern's "Current Students / Faculty & Staff / Alumni" strip).
const AUDIENCE_LINKS = [
  { label: "Current Students", to: "academics" },
  { label: "Faculty & Staff", to: "leadership" },
  { label: "Alumni", to: "alumni" },
  { label: "Directory", to: "contact" },
  { label: "Giving", to: "important-links" },
];

// Southwestern-style 6-slot main nav. Each item is either a direct link or
// a multi-column dropdown group whose columns render as flat lists.
export const NAV = [
  {
    label: "Academics",
    to: "academics",
    testid: "bul-nav-academics",
    columns: [
      {
        title: "Explore",
        links: [
          { label: "Programs & Courses", to: "academics" },
          { label: "Departments", to: "departments" },
          { label: "Academic Reports", to: "academic-reports" },
        ],
      },
      {
        title: "For Students",
        links: [
          { label: "Notices", to: "notices" },
          { label: "Executive Body", to: "executive-body" },
          { label: "Governing Body", to: "governing-body" },
        ],
      },
    ],
  },
  {
    label: "Admission & Aid",
    to: "admissions",
    testid: "bul-nav-admission",
    columns: [
      {
        title: "Apply",
        links: [
          { label: "Admissions Overview", to: "admissions" },
          { label: "Programs", to: "departments" },
          { label: "Contact Admissions", to: "contact" },
        ],
      },
      {
        title: "Cost & Career",
        links: [
          { label: "Fees & Financial Info", to: "admissions" },
          { label: "Placements", to: "careers" },
          { label: "Important Links", to: "important-links" },
        ],
      },
    ],
  },
  {
    label: "Life at Bulandshahr",
    to: "campus-engagements",
    testid: "bul-nav-life",
    columns: [
      {
        title: "Community",
        links: [
          { label: "Campus Engagements", to: "campus-engagements" },
          { label: "Committees & Cells", to: "committees" },
          { label: "Photo Gallery", to: "gallery" },
        ],
      },
      {
        title: "Campus",
        links: [
          { label: "Facilities", to: "facilities" },
          { label: "Careers @ MIT", to: "careers" },
        ],
      },
    ],
  },
  {
    label: "About",
    to: "about",
    testid: "bul-nav-about",
    columns: [
      {
        title: "Our Story",
        links: [
          { label: "About the Institute", to: "about" },
          { label: "Vision & Mission", to: "vision-mission" },
          { label: "Trust — GSM", to: "trust" },
        ],
      },
      {
        title: "People",
        links: [
          { label: "Leadership", to: "leadership" },
          { label: "Governing Body", to: "governing-body" },
          { label: "Contact Us", to: "contact" },
        ],
      },
    ],
  },
  {
    label: "News",
    to: "notices",
    testid: "bul-nav-news",
  },
  {
    label: "Alumni",
    to: "alumni",
    testid: "bul-nav-alumni",
  },
];

export default function BulLayout() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/public/website/bulandshahr");
        setData(data);
      } catch (e) {
        setErr(e?.response?.data?.detail || "Website not found");
      } finally { setLoading(false); }
    })();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    setMobileOpen(false);
  }, [location.pathname]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F5EE]">
        <div className="text-[#726C61] font-serif text-xl" style={{ fontFamily: FONT.serif }}>Loading…</div>
      </div>
    );
  }
  if (err || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8F5EE] px-6">
        <div className="text-center">
          <h1 className="font-serif text-3xl text-[#0F0F10]" style={{ fontFamily: FONT.serif }}>Website not found</h1>
          <p className="mt-2 text-[#726C61]">{err}</p>
        </div>
      </div>
    );
  }

  const inst = data.institute;
  const logoSrc = inst.logo_url || "/assets/mitcsn-logo.png";

  // Nav link — gold underline on hover / active, small caps, wide tracking.
  const linkClass = ({ isActive }) =>
    `relative uppercase text-[11px] tracking-[0.22em] font-semibold px-4 py-4 whitespace-nowrap transition-colors ${
      isActive ? "text-[#FDBB30]" : "text-white hover:text-[#FDBB30]"
    } after:content-[''] after:absolute after:left-4 after:right-4 after:-bottom-[1px] after:h-[3px] after:bg-[#FDBB30] after:transition-opacity ${
      isActive ? "after:opacity-100" : "after:opacity-0 hover:after:opacity-100"
    }`;

  return (
    <BulContext.Provider value={data}>
      <InstituteSeo slug="bulandshahr" inst={inst} themeColor={PALETTE.ink} ogImage="https://gsmandal.org/assets/og/mit-bulandshahr-og.jpg" />
      <Helmet prioritizeSeoTags>
        <link rel="preconnect" href="https://fonts.googleapis.com" />
        <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
        <link
          rel="stylesheet"
          href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400;1,600&family=Source+Sans+3:wght@300;400;500;600;700&display=swap"
        />
      </Helmet>
      <div
        className="min-h-screen flex flex-col bg-[#F8F5EE] text-[#2A2A2B]"
        style={{ fontFamily: FONT.sans }}
      >
        {/* --- Utility bar (ink) — audience links + institute switcher + ERP + search --- */}
        <div className="bg-[#0F0F10] text-[#D8D3C7] text-[11px] border-b border-[#1F1F20]" data-testid="bul-utility-bar">
          <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
            <div className="hidden md:flex items-center gap-5">
              {AUDIENCE_LINKS.map((a) => (
                <NavLink
                  key={a.to}
                  to={a.to}
                  className={({ isActive }) => `uppercase tracking-[0.2em] font-semibold transition-colors ${
                    isActive ? "text-[#FDBB30]" : "text-[#D8D3C7] hover:text-[#FDBB30]"
                  }`}
                  data-testid={`bul-utility-${slugify(a.label)}`}
                >
                  {a.label}
                </NavLink>
              ))}
            </div>
            <div className="flex items-center gap-4 ml-auto">
              <InstituteSwitcher currentSlug="bulandshahr" testidPrefix="bul-top-switcher" />
              <span className="text-[#3A3A3B] hidden md:inline">|</span>
              {[
                inst.facebook_url && { href: inst.facebook_url, Icon: Facebook, label: "Facebook", tid: "bul-top-facebook" },
                inst.instagram_url && { href: inst.instagram_url, Icon: Instagram, label: "Instagram", tid: "bul-top-instagram" },
                inst.linkedin_url && { href: inst.linkedin_url, Icon: Linkedin, label: "LinkedIn", tid: "bul-top-linkedin" },
                inst.youtube_url && { href: inst.youtube_url, Icon: Youtube, label: "YouTube", tid: "bul-top-youtube" },
              ].filter(Boolean).map(({ href, Icon, label, tid }) => (
                <a key={label} href={href} target="_blank" rel="noopener noreferrer" aria-label={label} data-testid={tid} className="hover:text-[#FDBB30]">
                  <Icon size={13} strokeWidth={1.6} />
                </a>
              ))}
              <span className="text-[#3A3A3B]">|</span>
              <a
                href="https://erp.mit.asia"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="bul-utility-erp"
                className="inline-flex items-center gap-1.5 uppercase tracking-[0.2em] font-semibold text-[#FDBB30] hover:text-white"
              >
                ERP Login <ExternalLink size={10} strokeWidth={2} />
              </a>
              {(data?.naac_documents || []).length > 0 && (
                <>
                  <span className="text-[#3A3A3B]">|</span>
                  <NavLink
                    to="naac"
                    data-testid="bul-utility-naac"
                    className="inline-flex items-center gap-1.5 bg-[#FDBB30] hover:bg-[#E19E14] text-[#0F0F10] font-bold tracking-widest px-2.5 py-0.5 text-[10px] uppercase"
                  >
                    NAAC
                  </NavLink>
                </>
              )}
            </div>
          </div>
        </div>

        {/* --- Paper masthead — logo + institute name + call CTA --- */}
        <div className="bg-[#F8F5EE] border-b border-[#D9D2C1]">
          <div className="max-w-7xl mx-auto px-4 py-6 md:py-8 flex items-center justify-between gap-4 flex-wrap">
            <NavLink to="" end className="flex items-center gap-5" data-testid="bul-logo-link" aria-label={inst.name}>
              <img src={logoSrc} alt={inst.name} className="h-[72px] md:h-[92px] w-auto object-contain" />
              <div className="leading-tight">
                <div
                  className="text-[#0F0F10] tracking-tight text-2xl md:text-4xl"
                  style={{ fontFamily: FONT.serif, fontWeight: 700 }}
                  data-testid="bul-header-name"
                >
                  {inst.name}
                </div>
                <div className="mt-1 text-[10px] tracking-[0.25em] uppercase text-[#726C61] font-semibold" data-testid="bul-header-trust">
                  Gramaudyogik Shikshan Mandal{inst.city ? ` · ${inst.city}` : ""}
                </div>
              </div>
            </NavLink>
            <div className="hidden md:flex items-center gap-6">
              {inst.admission_phone && (
                <a
                  href={`tel:${inst.admission_phone.replace(/\s+/g, "")}`}
                  data-testid="bul-header-phone"
                  className="inline-flex items-center gap-2 text-[13px] text-[#0F0F10] hover:text-[#E19E14]"
                >
                  <Phone size={14} strokeWidth={1.8} className="text-[#FDBB30]" />
                  {inst.admission_phone}
                </a>
              )}
            </div>
          </div>
        </div>

        {/* --- Main nav — ink strip, centered horizontal menu with gold underline --- */}
        <nav
          className="sticky top-0 z-40 bg-[#0F0F10] border-b border-[#FDBB30]/25"
          data-testid="bul-nav"
        >
          <div className="max-w-7xl mx-auto px-4">
            <div className="hidden xl:flex items-center justify-center flex-wrap">
              {NAV.map((n) => (
                <NavItem key={n.label} item={n} linkClass={linkClass} />
              ))}
              <div className="pl-3 py-3 flex items-center border-l border-[#1F1F20] ml-3">
                <button
                  type="button"
                  aria-label="Search"
                  data-testid="bul-nav-search"
                  className="text-white hover:text-[#FDBB30] p-1.5"
                  onClick={() => setInquiryOpen(true)}
                >
                  <Search size={16} strokeWidth={2} />
                </button>
              </div>
            </div>
            <div className="xl:hidden flex items-center justify-between py-3">
              <span className="text-white/80 font-medium text-sm uppercase tracking-[0.2em]">Menu</span>
              <button
                type="button"
                onClick={() => setMobileOpen((v) => !v)}
                className="p-2 text-[#FDBB30]"
                aria-label="Toggle menu"
                data-testid="bul-nav-toggle"
              >
                {mobileOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
            </div>
            {mobileOpen && (
              <div className="xl:hidden pb-4 space-y-1">
                {NAV.map((n) => (
                  <MobileNavGroup key={n.label} item={n} onNavigate={() => setMobileOpen(false)} />
                ))}
                <a
                  href="https://erp.mit.asia"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setMobileOpen(false)}
                  data-testid="bul-nav-erp-mobile"
                  className="mt-2 inline-flex w-full items-center justify-center gap-1.5 bg-[#FDBB30] hover:bg-[#E19E14] text-[#0F0F10] font-semibold text-xs uppercase tracking-wider px-3 py-2"
                >
                  ERP Login <ExternalLink size={11} strokeWidth={2} />
                </a>
              </div>
            )}
          </div>
        </nav>

        {/* Scrolling announcement bar (institute-master driven) */}
        {Array.isArray(inst.scrollers) && inst.scrollers.length > 0 && (
          <ScrollerBar items={inst.scrollers} />
        )}

        <main className="flex-1">
          <Outlet />
        </main>

        {inst.whatsapp_phone && (
          <WhatsAppFab
            phone={inst.whatsapp_phone.replace(/[^0-9]/g, "")}
            message={`Hello ${inst.name || "team"}, I have an admission inquiry.`}
          />
        )}
        <AdmissionInquiryFab onClick={() => setInquiryOpen(true)} />
        <AdmissionInquiryModal
          open={inquiryOpen}
          onClose={() => setInquiryOpen(false)}
          slug={inst.website_slug}
          departments={data.departments || []}
        />

        {/* --- Footer — Southwestern-style medium-grey with gold accents --- */}
        <footer className="bg-[#3A3A3A] text-[#D8D3C7] mt-20" data-testid="bul-footer">
          <div className="border-t-4 border-[#FDBB30]" />
          <div className="max-w-7xl mx-auto px-6 py-16 grid md:grid-cols-4 gap-10">
            <div className="md:col-span-1">
              <div className="flex items-center gap-3">
                <img src={logoSrc} alt="" className="h-14 w-14 object-contain bg-white/5 p-1" />
                <div
                  className="text-white text-xl leading-tight"
                  style={{ fontFamily: FONT.serif, fontWeight: 700 }}
                >
                  {inst.name}
                </div>
              </div>
              <p className="mt-4 text-[13px] leading-relaxed text-[#C0BAB0]">
                A flagship institution of Gramaudyogik Shikshan Mandal
                {inst.year_of_establishment ? `, established ${inst.year_of_establishment}` : ""}.
                {inst.approval_bodies ? ` ${inst.approval_bodies}.` : " AICTE Approved · AKTU Lucknow Affiliated."}
              </p>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#FDBB30] font-semibold mb-4">Contact</div>
              <div className="space-y-3 text-[13px] text-[#D8D3C7]">
                {(inst.address1 || inst.city) && (
                  <div className="flex items-start gap-2" data-testid="bul-footer-address">
                    <MapPin size={14} strokeWidth={1.6} className="text-[#FDBB30] mt-0.5 shrink-0" />
                    <span>{[inst.address1, inst.address2, [inst.city, inst.pincode && `– ${inst.pincode}`].filter(Boolean).join(" "), inst.state, inst.country || "INDIA"].filter(Boolean).join(", ")}</span>
                  </div>
                )}
                {(inst.contact_phone || inst.admission_phone) && (
                  <div className="flex items-start gap-2" data-testid="bul-footer-phone">
                    <Phone size={14} strokeWidth={1.6} className="text-[#FDBB30] mt-0.5" />
                    <span>{[...new Set([inst.contact_phone, inst.admission_phone].filter(Boolean))].join(" · ")}</span>
                  </div>
                )}
                {inst.email && (
                  <div className="flex items-start gap-2" data-testid="bul-footer-email">
                    <Mail size={14} strokeWidth={1.6} className="text-[#FDBB30] mt-0.5" />
                    <a href={`mailto:${inst.email}`} className="hover:text-[#FDBB30]">{inst.email}</a>
                  </div>
                )}
              </div>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#FDBB30] font-semibold mb-4">Explore</div>
              <ul className="grid grid-cols-1 gap-y-2 text-[13px]">
                {NAV.slice(1, 8).map((n) => (
                  <li key={n.to}>
                    <NavLink to={n.to} className="hover:text-[#FDBB30] transition-colors">{n.label}</NavLink>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <div className="text-[10px] tracking-[0.35em] uppercase text-[#FDBB30] font-semibold mb-4">Follow</div>
              <div className="flex items-center gap-3">
                {inst.facebook_url && <a href={inst.facebook_url} target="_blank" rel="noopener noreferrer" aria-label="Facebook" className="hover:text-[#FDBB30]"><Facebook size={16} /></a>}
                {inst.instagram_url && <a href={inst.instagram_url} target="_blank" rel="noopener noreferrer" aria-label="Instagram" className="hover:text-[#FDBB30]"><Instagram size={16} /></a>}
                {inst.linkedin_url && <a href={inst.linkedin_url} target="_blank" rel="noopener noreferrer" aria-label="LinkedIn" className="hover:text-[#FDBB30]"><Linkedin size={16} /></a>}
                {inst.youtube_url && <a href={inst.youtube_url} target="_blank" rel="noopener noreferrer" aria-label="YouTube" className="hover:text-[#FDBB30]"><Youtube size={16} /></a>}
              </div>
              <button
                type="button"
                onClick={() => setInquiryOpen(true)}
                data-testid="bul-footer-apply"
                className="mt-6 inline-flex items-center gap-2 bg-[#FDBB30] hover:bg-[#E19E14] text-[#0F0F10] font-semibold uppercase tracking-[0.2em] text-[11px] px-5 py-3"
              >
                Apply Now <ArrowRight size={13} strokeWidth={2.4} />
              </button>
            </div>
          </div>
          <div className="border-t border-[#4A4A4A] py-5 text-center text-[11px] text-[#C0BAB0] uppercase tracking-[0.25em]">
            © {new Date().getFullYear()} {inst.name} · An institution under Gramaudyogik Shikshan Mandal
          </div>
        </footer>
      </div>
    </BulContext.Provider>
  );
}

// ---------- Southwestern-style multi-column dropdown --------------------

function NavItem({ item, linkClass }) {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);
  const closeTimer = useRef(null);
  useEffect(() => {
    const onDoc = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const cancelClose = () => { if (closeTimer.current) { clearTimeout(closeTimer.current); closeTimer.current = null; } };
  const scheduleClose = () => { cancelClose(); closeTimer.current = setTimeout(() => setOpen(false), 180); };

  const hasDropdown = Array.isArray(item.columns) && item.columns.length > 0;

  if (!hasDropdown) {
    return (
      <NavLink
        to={item.to}
        data-testid={item.testid}
        className={linkClass}
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div
      ref={wrapRef}
      className="relative"
      onMouseEnter={() => { cancelClose(); setOpen(true); }}
      onMouseLeave={scheduleClose}
      data-testid={`${item.testid}-wrap`}
    >
      <NavLink
        to={item.to}
        data-testid={item.testid}
        className={({ isActive }) => `${linkClass({ isActive })} inline-flex items-center gap-1`}
        onClick={() => setOpen(false)}
      >
        {item.label}
        <ChevronDown size={12} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </NavLink>
      {open && (
        <div
          className="absolute left-0 top-full pt-1 min-w-[420px] z-50"
          data-testid={`${item.testid}-menu`}
          onMouseEnter={cancelClose}
          onMouseLeave={scheduleClose}
        >
          <div className="bg-[#0F0F10] ring-1 ring-[#FDBB30]/25 py-5 px-5 grid grid-cols-2 gap-6">
            {item.columns.map((col) => (
              <div key={col.title} className="min-w-[160px]">
                <div className="text-[9px] tracking-[0.35em] uppercase text-[#FDBB30] font-bold mb-2">
                  {col.title}
                </div>
                <ul className="space-y-1">
                  {col.links.map((l) => (
                    <li key={`${item.label}-${l.to}`}>
                      <NavLink
                        to={l.to}
                        data-testid={`${item.testid}-${l.to}`}
                        onClick={() => setOpen(false)}
                        className="block px-2 py-1.5 text-sm text-white hover:text-[#FDBB30] border-l-2 border-transparent hover:border-[#FDBB30] hover:pl-3 transition-all"
                      >
                        {l.label}
                      </NavLink>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function MobileNavGroup({ item, onNavigate }) {
  const [open, setOpen] = useState(false);
  const hasDropdown = Array.isArray(item.columns) && item.columns.length > 0;
  if (!hasDropdown) {
    return (
      <NavLink
        to={item.to}
        onClick={onNavigate}
        data-testid={`${item.testid}-mobile`}
        className="block px-4 py-2.5 text-white hover:bg-[#1F1F20] hover:text-[#FDBB30] text-[11px] tracking-[0.22em] uppercase font-semibold"
      >
        {item.label}
      </NavLink>
    );
  }
  return (
    <div>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${item.testid}-mobile-toggle`}
        className="w-full flex items-center justify-between px-4 py-2.5 text-white hover:bg-[#1F1F20] hover:text-[#FDBB30] text-[11px] tracking-[0.22em] uppercase font-semibold"
      >
        <span>{item.label}</span>
        <ChevronDown size={13} strokeWidth={2} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pl-4 pb-2 space-y-2 bg-[#141312]">
          {item.columns.map((col) => (
            <div key={col.title}>
              <div className="text-[9px] tracking-[0.3em] uppercase text-[#FDBB30] font-bold px-2 pt-2">{col.title}</div>
              {col.links.map((l) => (
                <NavLink
                  key={`m-${l.to}`}
                  to={l.to}
                  onClick={onNavigate}
                  className="block pl-4 pr-3 py-1.5 text-sm text-white/85 hover:text-[#FDBB30]"
                >
                  {l.label}
                </NavLink>
              ))}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ---------- Floating action buttons ---------------------------------------

function WhatsAppFab({ phone, message }) {
  const href = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label="Chat on WhatsApp for admission inquiry"
      data-testid="bul-whatsapp-fab"
      className="fixed z-40 bottom-24 right-5 sm:bottom-28 sm:right-7 flex items-center gap-2 group"
    >
      <span className="absolute inline-flex h-14 w-14 rounded-full bg-green-500 opacity-40 animate-ping pointer-events-none right-0 bottom-0" />
      <span className="hidden sm:inline-flex bg-white text-[#0F0F10] text-xs font-semibold px-3 py-2 rounded-full shadow-md ring-1 ring-[#D9D2C1] opacity-0 group-hover:opacity-100 transition-opacity">
        Admission inquiry
      </span>
      <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#25D366] hover:bg-[#1ebe5d] text-white shadow-lg ring-4 ring-white transition-transform group-hover:scale-105">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-7 h-7" aria-hidden="true">
          <path d="M20.52 3.48A11.78 11.78 0 0 0 12.06 0C5.5 0 .15 5.34.15 11.9c0 2.1.55 4.15 1.6 5.96L0 24l6.32-1.66a11.86 11.86 0 0 0 5.73 1.46h.01c6.56 0 11.91-5.34 11.91-11.9 0-3.18-1.24-6.17-3.45-8.42ZM12.06 21.8h-.01a9.85 9.85 0 0 1-5.03-1.38l-.36-.21-3.75.98 1-3.65-.23-.37a9.86 9.86 0 0 1-1.51-5.27c0-5.45 4.44-9.89 9.9-9.89 2.64 0 5.13 1.03 7 2.9a9.87 9.87 0 0 1 2.9 7c0 5.46-4.44 9.89-9.9 9.89Zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.77.97-.95 1.17-.17.2-.35.22-.65.07-.3-.15-1.25-.46-2.39-1.47-.88-.78-1.48-1.75-1.65-2.05-.17-.3-.02-.46.13-.61.13-.13.3-.35.45-.52.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.9-2.18-.24-.58-.48-.5-.66-.5l-.56-.01c-.2 0-.5.07-.76.37-.27.3-1.02.99-1.02 2.42 0 1.43 1.04 2.81 1.18 3 .15.2 2.06 3.15 5 4.42.7.3 1.24.48 1.66.62.7.22 1.34.19 1.84.12.56-.08 1.76-.72 2-1.42.25-.7.25-1.3.17-1.42-.07-.13-.27-.2-.57-.35Z" />
        </svg>
      </span>
    </a>
  );
}

function AdmissionInquiryFab({ onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label="Open admission inquiry form"
      data-testid="bul-admission-inquiry-fab"
      className="fixed z-40 bottom-5 right-5 sm:bottom-7 sm:right-7 inline-flex items-center gap-2 group"
    >
      <span className="hidden sm:inline-flex bg-white text-[#0F0F10] text-xs font-semibold px-3 py-2 rounded-full shadow-md ring-1 ring-[#D9D2C1] opacity-0 group-hover:opacity-100 transition-opacity">
        Contact Our Admission Team
      </span>
      <span className="relative inline-flex items-center justify-center h-14 w-14 rounded-full bg-[#FDBB30] hover:bg-[#E19E14] text-[#0F0F10] shadow-lg ring-4 ring-white transition-transform group-hover:scale-105">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" className="w-7 h-7" aria-hidden="true">
          <path d="M22 10v6" />
          <path d="M2 10l10-5 10 5-10 5z" />
          <path d="M6 12v5c3 3 9 3 12 0v-5" />
        </svg>
      </span>
    </button>
  );
}

function ScrollerBar({ items }) {
  const list = items || [];
  if (list.length === 0) return null;
  const renderSegment = (s, i) => {
    const inner = (
      <span className="inline-block whitespace-nowrap px-3">
        <span className="opacity-80 mr-2 align-middle">▸</span>
        {s.text}
      </span>
    );
    if (s.url) {
      return (
        <a
          key={`${s.id}-${i}`}
          href={s.url}
          target="_blank"
          rel="noopener noreferrer"
          className="hover:underline focus:underline outline-none"
          data-testid={`bul-scroller-link-${s.id}`}
        >
          {inner}
        </a>
      );
    }
    return <span key={`${s.id}-${i}`} data-testid={`bul-scroller-text-${s.id}`}>{inner}</span>;
  };
  const duration = Math.max(20, Math.min(60, list.length * 12));
  return (
    <div className="bg-[#FDBB30] text-[#0F0F10] overflow-hidden" data-testid="bul-scroller-bar">
      <style>{`
        @keyframes bul-marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }
        [data-testid="bul-scroller-bar"]:hover [data-testid="bul-scroller-track"] { animation-play-state: paused !important; }
      `}</style>
      <div className="max-w-7xl mx-auto px-3 py-1.5 flex items-center gap-3">
        <span className="hidden sm:inline-flex text-[10px] tracking-[0.3em] uppercase font-bold bg-[#0F0F10] text-[#FDBB30] px-2 py-0.5 shrink-0">
          Announcement
        </span>
        <div className="flex-1 overflow-hidden">
          <span
            data-testid="bul-scroller-track"
            className="inline-block whitespace-nowrap text-sm font-semibold"
            style={{ animation: `bul-marquee ${duration}s linear infinite` }}
          >
            {list.map(renderSegment)}
            {list.map((s, i) => renderSegment(s, `copy-${i}`))}
          </span>
        </div>
      </div>
    </div>
  );
}
